import { useProcessoDetail } from '@/modules/kanban/api';
import { ArrendamentoImoveis } from './ArrendamentoImoveis';
import { imoveisDoContrato, tituloImoveis, matriculasImoveis, municipiosImoveis } from './arrendamento-imoveis-helpers';
import React, { useState } from 'react';
import {
  Archive,
  Banknote,
  Building2,
  CircleCheck,
  FolderOpen,
  Handshake,
  Inbox,
  Landmark,
  Leaf,
  Map,
  MapPin,
  Pencil,
  Plus,
  SatelliteDish,
  ScanLine,
  Trash2,
  TriangleAlert,
  User,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Button, Chip, Modal } from '@/ui/primitives';
import { formatBRL, formatDateBR, formatName } from '@/lib/format';
import { useClienteAmbiental, useDocumentos } from '../api';
import type {
  ArrendamentoResumo,
  ClienteDetail,
  ImovelResumo,
  ProcessoAmbiental,
  ProcessoResumo,
} from '../schemas';
import { SectionHeader, KanbanLane } from '@/ui/primitives';
import { Accordion, ImovelDetailModal } from './ImovelDetailModal';

interface Props {
  cliente: ClienteDetail;
  onAddImovel?: () => void;
  onEditImovel?: (im: ImovelResumo) => void;
  onDeleteImovel?: (im: ImovelResumo) => void;
}

function normalizeProgramaLinha(value: string | null | undefined) {
  if (!value) return null;
  return value.replace(/\s+ASTEC$/i, '').trim();
}

export function ClienteResumo({ cliente, onAddImovel, onEditImovel, onDeleteImovel }: Props) {
  const { data: ambiental = [] } = useClienteAmbiental(cliente.cpf);
  useDocumentos(cliente.cpf); // mantém o pré-fetch — DocumentoPreviewModal lê pelo cache
  const [imovelDetail, setImovelDetail] = useState<ImovelResumo | null>(null);
  const [operacaoDetail, setOperacaoDetail] = useState<ProcessoResumo | null>(null);
  const [ambientalDetail, setAmbientalDetail] = useState<ProcessoAmbiental | null>(null);
  const [geoDetail, setGeoDetail] = useState<ProcessoResumo | null>(null);
  const [arrendamentoDetail, setArrendamentoDetail] = useState<ArrendamentoResumo | null>(null);
  const [previewDocId, setPreviewDocId] = useState<string | number | null>(null);

  // Hectares do cliente: terra própria (imóveis) e arrendada (contratos onde
  // ele é arrendatário). Mostrados separados no header de cada seção.
  const fmtHa = (n: number) => `${Math.round(n).toLocaleString('pt-BR')} ha`;
  const areaArrendada = (cliente.arrendamentos ?? []).reduce((s, a) => s + (a.area_ha ?? 0), 0);

  return (
    <div className="flex gap-3 overflow-x-auto pb-2 items-start">
      <Section
        title="IMÓVEIS"
        icon={Building2}
        count={cliente.imoveis.length}
        action={
          onAddImovel ? (
            <Button variant="ghost" size="sm" onClick={onAddImovel}>
              <Plus size={12} strokeWidth={1.5} />
              Adicionar
            </Button>
          ) : null
        }
      >
        {cliente.imoveis.length === 0 ? (
          <p className="text-xs text-text-muted">Nenhum imóvel cadastrado.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {cliente.imoveis.map((im) => {
              const denominacao = formatName(im.denominacao) || `Imóvel ${im.id}`;
              const municipioUf = im.municipio
                ? `${formatName(im.municipio)}${im.uf ? `/${im.uf}` : ''}`
                : null;
              return (
                <li
                  key={im.id}
                  onClick={() => setImovelDetail(im)}
                  className="relative w-full text-left rounded-lg bg-surface px-[14px] py-3 flex flex-col gap-1.5 shadow-[0_1px_3px_rgba(0,0,0,0.04)] cursor-pointer select-none transition-[box-shadow,transform,border-color] duration-150 hover:shadow-[0_2px_8px_rgba(0,0,0,0.06)] hover:-translate-y-px group"
                >
                  <div className="flex items-start gap-2">
                    <span
                      className="flex-1 min-w-0 font-semibold text-text leading-snug line-clamp-2"
                      style={{ fontSize: '0.85rem' }}
                    >
                      {denominacao}
                    </span>
                    <div className="flex items-center gap-0.5 shrink-0">
                      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        {onEditImovel && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onEditImovel(im);
                            }}
                            aria-label="Editar imóvel"
                            className="p-1 text-text-muted hover:text-text rounded hover:bg-surface2"
                          >
                            <Pencil size={12} strokeWidth={1.5} />
                          </button>
                        )}
                        {onDeleteImovel && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onDeleteImovel(im);
                            }}
                            aria-label="Excluir imóvel"
                            className="p-1 text-text-muted hover:text-danger rounded hover:bg-surface2"
                          >
                            <Trash2 size={12} strokeWidth={1.5} />
                          </button>
                        )}
                      </div>
                      {im.car_codigo && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(`/api/v2/imoveis/${im.id}/mapa-desenho`, '_blank', 'noopener');
                          }}
                          aria-label="Ver no mapa"
                          title="Ver no mapa"
                          className="p-1 rounded text-accent bg-[var(--accent-soft)] hover:bg-[rgba(233,106,69,0.18)] transition-colors"
                        >
                          <Map size={13} strokeWidth={1.6} />
                        </button>
                      )}
                    </div>
                  </div>

                  {im.matricula && (
                    <span className="text-text-2 line-clamp-1" style={{ fontSize: '0.78rem' }}>
                      Matrícula {im.matricula}
                    </span>
                  )}

                  <div className="flex flex-wrap gap-1 mt-1">
                    {im.area_total_ha != null && (
                      <Chip tone="neutral" size="sm">
                        {im.area_total_ha.toLocaleString('pt-BR')} ha
                      </Chip>
                    )}
                    {municipioUf && (
                      <Chip tone="info" size="sm">
                        {municipioUf}
                      </Chip>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Section>

      <Section
        title="ARRENDAMENTO"
        icon={Handshake}
        count={cliente.arrendamentos?.length ?? 0}
        subtitle={areaArrendada > 0 ? fmtHa(areaArrendada) : undefined}
      >
        {!cliente.arrendamentos || cliente.arrendamentos.length === 0 ? (
          <p className="text-xs text-text-muted">Nenhum arrendamento cadastrado.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {cliente.arrendamentos.map((arr) => (
              <ArrendamentoCard
                key={arr.id}
                arrendamento={arr}
                onClick={() => setArrendamentoDetail(arr)}
              />
            ))}
          </ul>
        )}
      </Section>

      <Section title="AMBIENTAL" icon={Leaf} count={ambiental.length}>
        {ambiental.length === 0 ? (
          <p className="text-xs text-text-muted">Nenhum processo ambiental.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {ambiental.map((p) => {
              const tipoLabel = labelTipoAmbiental(p.tipo_processo);
              const matricula = p.imovel_matricula ?? null;
              const faseNome = faseNomeAmbiental(p.kanban_coluna);
              return (
                <li
                  key={p.id}
                  onClick={() => setAmbientalDetail(p)}
                  className="rounded-lg bg-surface px-[14px] py-3 flex flex-col gap-1.5 shadow-[0_1px_3px_rgba(0,0,0,0.04)] cursor-pointer select-none transition-[box-shadow,transform,border-color] duration-150 hover:shadow-[0_2px_8px_rgba(0,0,0,0.06)] hover:-translate-y-px"
                >
                  <span
                    className="self-start text-[0.65rem] font-bold px-[6px] py-[2px] rounded-full"
                    style={{
                      background: 'rgba(16,185,129,.12)',
                      color: 'var(--ok)',
                      letterSpacing: '0.04em',
                    }}
                  >
                    {tipoLabel}
                  </span>
                  {matricula && (
                    <span
                      className="font-semibold text-text line-clamp-1"
                      style={{ fontSize: '0.85rem' }}
                    >
                      Matrícula {matricula}
                    </span>
                  )}
                  <div className="flex flex-wrap gap-1 mt-1">
                    {faseNome && (
                      <Chip tone="info" size="sm">
                        {faseNome}
                      </Chip>
                    )}
                    {p.status && (
                      <Chip tone={statusToneAmbiental(p.status)} size="sm">
                        {labelStatusAmbiental(p.status)}
                      </Chip>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Section>

      <Section title="OPERAÇÕES" icon={Banknote} count={cliente.operacoes_credito.length}>
        {cliente.operacoes_credito.length === 0 ? (
          <p className="text-xs text-text-muted">Nenhuma operação de crédito.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {cliente.operacoes_credito.map((op) => {
              const instituicao = formatName(op.instituicao ?? '') || `Operação #${op.id}`;
              const demanda = normalizeProgramaLinha(op.programa) || op.finalidade || op.tipo_processo || null;
              const valorFmt = op.valor != null ? formatBRL(op.valor) : null;
              const prazoFmt = op.prazo_meses != null ? `${op.prazo_meses}m` : null;
              const statusTone = urgencyTone(op.urgencia);
              return (
                <li
                  key={op.id}
                  onClick={() => setOperacaoDetail(op)}
                  className="rounded-lg bg-surface px-[14px] py-3 flex flex-col gap-1.5 shadow-[0_1px_3px_rgba(0,0,0,0.04)] cursor-pointer select-none transition-[box-shadow,transform,border-color] duration-150 hover:shadow-[0_2px_8px_rgba(0,0,0,0.06)] hover:-translate-y-px"
                >
                  <span
                    className="font-semibold text-text leading-snug line-clamp-2"
                    style={{ fontSize: '0.85rem' }}
                  >
                    {instituicao}
                  </span>
                  {demanda && (
                    <span className="text-text-2 line-clamp-1" style={{ fontSize: '0.78rem' }}>
                      {demanda}
                    </span>
                  )}
                  <span className="text-[11px] text-text-muted">
                    {op.card_codigo || `CARD #${op.id}`}
                    {op.fase_atual ? ` · ${op.fase_atual}` : ''}
                    {op.tempo_fase_horas != null ? ` · ${op.tempo_fase_horas}h` : ''}
                  </span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {valorFmt && (
                      <Chip tone="ok" size="sm">
                        {valorFmt}
                      </Chip>
                    )}
                    {prazoFmt && (
                      <Chip tone="info" size="sm">
                        {prazoFmt}
                      </Chip>
                    )}
                    {op.urgencia && (
                      <Chip tone={statusTone} size="sm">
                        {op.urgencia}
                      </Chip>
                    )}
                  </div>
                  <div className="mt-1 grid gap-0.5 text-[11px] text-text-muted">
                    <span>Responsável: {op.responsavel_nome || 'Não definido'}</span>
                    <span>Próxima ação: {op.proxima_acao || 'Não definida'}</span>
                    <span>Última movimentação: {op.ultima_movimentacao ? formatDateBR(op.ultima_movimentacao) : '—'}</span>
                  </div>
                  {op.card_url && (
                    <a href={op.card_url} onClick={(event) => event.stopPropagation()} className="mt-1 self-start text-xs font-semibold text-accent hover:underline">
                      Abrir CARD →
                    </a>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Section>

      <Section title="FUNDIÁRIO" icon={ScanLine} count={cliente.processos_geo.length}>
        {cliente.processos_geo.length === 0 ? (
          <p className="text-xs text-text-muted">Nenhum processo fundiário.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {cliente.processos_geo.map((p) => (
              <GeoCard key={p.id} p={p} onClick={() => setGeoDetail(p)} />
            ))}
          </ul>
        )}
      </Section>

      <ImovelDetailModal
        imovel={imovelDetail}
        cpfDono={cliente.cpf}
        nomeDono={cliente.nome}
        onClose={() => setImovelDetail(null)}
      />

      <OperacaoDetailModal op={operacaoDetail} onClose={() => setOperacaoDetail(null)} />

      <AmbientalDetailModal proc={ambientalDetail} onClose={() => setAmbientalDetail(null)} />

      <GeoDetailModal proc={geoDetail} onClose={() => setGeoDetail(null)} />

      <ArrendamentoDetailModal
        arrendamento={arrendamentoDetail}
        onClose={() => setArrendamentoDetail(null)}
      />

      {previewDocId != null && (
        <DocumentoPreviewModal docId={previewDocId} onClose={() => setPreviewDocId(null)} />
      )}
    </div>
  );
}

function GeoDetailModal({ proc, onClose }: { proc: ProcessoResumo | null; onClose: () => void }) {
  const open = proc !== null;
  const tipo = proc?.tipo_processo
    ? (GEO_TIPO_LABEL[proc.tipo_processo] ?? proc.tipo_processo)
    : 'Processo fundiário';
  const matricula = proc?.imovel_matricula ?? null;
  const title = matricula ? `${tipo} · Matrícula ${matricula}` : tipo;
  const colNum = proc ? Number(proc.kanban_coluna) || 0 : 0;
  const colNome = GEO_COL_NOMES[colNum];
  const resumo = proc?.checklist_resumo ?? null;
  return (
    <Modal open={open} onOpenChange={(v) => !v && onClose()} title={title} size="xl">
      {proc && (
        <div className="px-6 pb-6">
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <DetailRow label="Tipo" value={tipo} />
            <DetailRow label="Fase atual" value={colNome ?? `Coluna ${colNum}`} />
            <DetailRow label="Matrícula" value={matricula ?? '—'} />
            <DetailRow label="Imóvel" value={proc.imovel_denominacao ?? '—'} />
            <DetailRow label="Município" value={proc.imovel_municipio ?? '—'} />
            <DetailRow label="Cartório" value={proc.cartorio ?? '—'} />
            <DetailRow label="Responsável técnico" value={proc.responsavel_tecnico_nome ?? '—'} />
            <DetailRow
              label="Dias na fase"
              value={proc.dias_na_coluna != null ? `${proc.dias_na_coluna} dias` : '—'}
            />
            <DetailRowChip
              label="Status"
              value={statusLabelGeo(proc.status)}
              tone={statusToneGeo(proc.status)}
            />
            <DetailRow
              label="Documentos"
              value={
                resumo
                  ? `${resumo.recebidos}/${resumo.total} recebidos${resumo.pendentes ? ` · ${resumo.pendentes} pendentes` : ''}`
                  : '—'
              }
            />
            {proc.pendencias && (
              <div className="col-span-2">
                <DetailRow label="Pendências" value={proc.pendencias} />
              </div>
            )}
            <DetailRow label="ID do processo" value={`#${proc.id}`} />
          </dl>
        </div>
      )}
    </Modal>
  );
}

function AmbientalDetailModal({
  proc,
  onClose,
}: {
  proc: ProcessoAmbiental | null;
  onClose: () => void;
}) {
  const open = proc !== null;
  const tipo = proc ? labelTipoAmbiental(proc.tipo_processo) : 'Processo ambiental';
  const matricula = proc?.imovel_matricula ?? null;
  const title = matricula ? `${tipo} · Matrícula ${matricula}` : tipo;
  return (
    <Modal open={open} onOpenChange={(v) => !v && onClose()} title={title} size="xl">
      {proc && (
        <div className="px-6 pb-6">
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <DetailRow label="Tipo" value={tipo} />
            <DetailRow label="Matrícula" value={matricula ?? '—'} />
            <DetailRow label="Fase atual" value={faseNomeAmbiental(proc.kanban_coluna) ?? '—'} />
            <DetailRowChip
              label="Status"
              value={proc.status ? labelStatusAmbiental(proc.status) : null}
              tone={statusToneAmbiental(proc.status)}
            />
            <DetailRow label="Código CAR" value={proc.codigo_car ?? '—'} />
            <DetailRow
              label="Validade da licença"
              value={proc.data_validade_licenca ? formatDateBR(proc.data_validade_licenca) : '—'}
            />
            <DetailRow label="ID do processo" value={`#${proc.id}`} />
          </dl>
        </div>
      )}
    </Modal>
  );
}

function OperacaoDetailModal({ op, onClose }: { op: ProcessoResumo | null; onClose: () => void }) {
  const { data: detail, isLoading, error } = useProcessoDetail('credito', op?.id ?? null);
  const field = (key: string) => typeof detail?.[key] === 'string' ? String(detail[key]) : '';

  const open = op !== null;
  const title = op ? formatName(op.instituicao ?? '') || `Operação #${op.id}` : 'Operação';
  return (
    <Modal open={open} onOpenChange={(v) => !v && onClose()} title={title} size="xl">
      {op && (
        <div className="px-6 pb-6">
          {isLoading && <p className="mb-3 text-sm text-text-muted">Carregando demanda do card…</p>}
          {error && <p className="mb-3 text-sm text-danger">Não foi possível carregar a demanda. Use o atalho para abrir o card.</p>}
          {field('proxima_acao') && <div className="mb-3"><DetailRow label="Próxima ação" value={field('proxima_acao')} /></div>}
          {field('observacoes') && <div className="mb-4 whitespace-pre-wrap"><DetailRow label="Demanda / observações" value={field('observacoes')} /></div>}
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <DetailRow label="Instituição" value={formatName(op.instituicao ?? '') || '—'} />
            <DetailRow
              label="Programa / linha"
              value={normalizeProgramaLinha(op.programa) ?? '—'}
            />
            <DetailRow label="Finalidade" value={op.finalidade ?? '—'} />
            <DetailRow
              label="Valor solicitado"
              value={op.valor != null ? formatBRL(op.valor) : '—'}
            />
            <DetailRow
              label="Valor aprovado"
              value={op.valor_aprovado != null ? formatBRL(op.valor_aprovado) : '—'}
            />
            <DetailRow
              label="Prazo"
              value={op.prazo_meses != null ? `${op.prazo_meses} meses` : '—'}
            />
            <DetailRow
              label="Taxa de juros"
              value={op.taxa_juros != null ? `${op.taxa_juros}% a.a.` : '—'}
            />
            <DetailRowChip
              label="Status"
              value={op.status ? labelStatusOperacao(op.status) : null}
              tone={statusToneOperacao(op.status)}
            />
            <DetailRow label="ID do processo" value={`#${op.id}`} />
            <div className="col-span-2 pt-3">
              <a
                href={`/credito?pipe=${encodeURIComponent(field('tipo_processo') || op.tipo_processo || 'triagem_credito')}&card=${encodeURIComponent(op.id)}`}
                className="inline-flex items-center rounded-md border border-border px-4 py-2 font-medium text-text hover:bg-surface2"
              >
                Abrir card #{op.id}
              </a>
            </div>
          </dl>
        </div>
      )}
    </Modal>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5 border-b border-border pb-2 last:border-0">
      <dt className="text-[10px] uppercase tracking-wider text-text-muted">{label}</dt>
      <dd className="text-text tabular-nums">{value}</dd>
    </div>
  );
}

// Variante do DetailRow que renderiza o valor como Chip — usado pra status,
// que segue o padrão visual de etiqueta no resto do app.
function DetailRowChip({
  label,
  value,
  tone,
}: {
  label: string;
  value: string | null | undefined;
  tone: 'neutral' | 'accent' | 'ok' | 'warn' | 'danger' | 'info';
}) {
  return (
    <div className="flex flex-col gap-1 border-b border-border pb-2 last:border-0">
      <dt className="text-[10px] uppercase tracking-wider text-text-muted">{label}</dt>
      <dd>
        {value ? (
          <Chip tone={tone} size="sm">
            {value}
          </Chip>
        ) : (
          <span className="text-text-muted">—</span>
        )}
      </dd>
    </div>
  );
}

function statusLabelGeo(s: string | null | undefined): string | null {
  if (!s) return null;
  switch (s) {
    case 'em_andamento':
      return 'Em andamento';
    case 'concluido':
      return 'Concluído';
    case 'cancelado':
      return 'Cancelado';
    case 'pendente':
      return 'Pendente';
    case 'arquivado':
      return 'Arquivado';
    default:
      return s;
  }
}

function statusToneGeo(
  s: string | null | undefined,
): 'neutral' | 'accent' | 'ok' | 'warn' | 'danger' | 'info' {
  switch (s) {
    case 'concluido':
      return 'ok';
    case 'em_andamento':
      return 'info';
    case 'pendente':
      return 'warn';
    case 'cancelado':
    case 'arquivado':
      return 'danger';
    default:
      return 'neutral';
  }
}

const AMBIENTAL_TIPO_LABEL: Record<string, string> = {
  ambiental_car: 'CAR',
  ambiental_pra: 'PRA',
  ambiental_desembargo: 'Desembargo',
  ambiental_limpeza: 'Limpeza de pastagem',
  ambiental_lp: 'Licença prévia (LP)',
  ambiental_li: 'Licença de instalação (LI)',
  ambiental_lo: 'Licença de operação (LO)',
};

function labelTipoAmbiental(t: string | null | undefined): string {
  if (!t) return 'Processo ambiental';
  return AMBIENTAL_TIPO_LABEL[t] ?? t.replace(/^ambiental_/, '').toUpperCase();
}

const AMBIENTAL_FASE_NOME: Record<number, string> = {
  // PRA (30-37)
  30: 'Entrada',
  31: 'Documentação',
  32: 'Definição da modalidade',
  33: 'Projeto técnico/PRADA',
  34: 'Protocolo SEDAM',
  35: 'Análise SEDAM',
  36: 'Termo de compromisso',
  37: 'Concluído',
  // CAR (40-46)
  40: 'Entrada',
  41: 'Documentação',
  42: 'Levantamento de campo',
  43: 'Mapeamento/Shape',
  44: 'Inscrição SICAR',
  45: 'Análise SEDAM',
  46: 'Concluído',
  // Desembargo (50-57)
  50: 'Entrada',
  51: 'Documentação',
  52: 'Análise do auto/embargo',
  53: 'Defesa técnica/Laudo',
  54: 'Protocolo',
  55: 'Análise do órgão',
  56: 'Vistoria',
  57: 'Concluído',
  // Limpeza (60-66)
  60: 'Entrada',
  61: 'Documentação',
  62: 'Projeto técnico/ART',
  63: 'Mapa e Shape',
  64: 'Protocolo SEDAM',
  65: 'Análise',
  66: 'Concluído',
  // LP (70-77)
  70: 'Entrada',
  71: 'Documentação',
  72: 'RCA',
  73: 'Publicação em jornal',
  74: 'Protocolo prefeitura',
  75: 'Análise',
  76: 'Vistoria',
  77: 'LP emitida',
  // LI (80-87)
  80: 'Entrada',
  81: 'Documentação',
  82: 'Projetos técnicos + ART',
  83: 'PCA/Estudos',
  84: 'Protocolo prefeitura',
  85: 'Análise',
  86: 'Vistoria',
  87: 'LI emitida',
  // LO (90-97)
  90: 'Entrada',
  91: 'Documentação',
  92: 'AVCIP bombeiros',
  93: 'Relatório de conformidade',
  94: 'Protocolo prefeitura',
  95: 'Análise',
  96: 'Vistoria',
  97: 'LO emitida',
};

function faseNomeAmbiental(col: number | string | null | undefined): string | null {
  if (col == null) return null;
  const n = typeof col === 'number' ? col : Number(col);
  if (!Number.isFinite(n)) return null;
  return AMBIENTAL_FASE_NOME[n] ?? `Fase ${n}`;
}

const AMBIENTAL_STATUS_LABEL: Record<string, string> = {
  em_andamento: 'em andamento',
  concluido: 'concluído',
  cancelado: 'cancelado',
  suspenso: 'suspenso',
  pendente: 'pendente',
};

function labelStatusAmbiental(s: string): string {
  return AMBIENTAL_STATUS_LABEL[s] ?? s.replace(/_/g, ' ');
}

function statusToneAmbiental(
  s: string | null | undefined,
): 'ok' | 'accent' | 'info' | 'warn' | 'danger' | 'neutral' {
  switch (s) {
    case 'concluido':
      return 'ok';
    case 'em_andamento':
      return 'info';
    case 'pendente':
      return 'warn';
    case 'suspenso':
    case 'cancelado':
      return 'danger';
    default:
      return 'neutral';
  }
}

const STATUS_OP_LABEL: Record<string, string> = {
  em_montagem: 'em montagem',
  protocolado: 'protocolado',
  em_analise: 'em análise',
  aprovado: 'aprovado',
  contratado: 'contratado',
  concluido: 'concluído',
  cancelado: 'cancelado',
};

function labelStatusOperacao(s: string): string {
  return STATUS_OP_LABEL[s] ?? s.replace(/_/g, ' ');
}

function statusToneOperacao(
  s: string | null | undefined,
): 'ok' | 'accent' | 'info' | 'warn' | 'danger' | 'neutral' {
  switch (s) {
    case 'aprovado':
    case 'contratado':
    case 'concluido':
      return 'ok';
    case 'em_analise':
    case 'protocolado':
      return 'info';
    case 'em_montagem':
      return 'accent';
    case 'cancelado':
      return 'danger';
    default:
      return 'neutral';
  }
}

function urgencyTone(
  urgencia: string | null | undefined,
): 'ok' | 'warn' | 'danger' | 'neutral' {
  switch (urgencia) {
    case 'Normal': return 'ok';
    case 'Atenção': return 'warn';
    case 'Urgente':
    case 'Crítico': return 'danger';
    default: return 'neutral';
  }
}

const GEO_COL_NOMES: Record<number, string> = {
  1: 'Entrada',
  2: 'Documentação',
  3: 'Campo',
  4: 'SIGEF',
  5: 'Montagem',
  6: 'Cartório',
  7: 'Concluído',
  10: 'CCIR Docs',
  11: 'CCIR Cartório',
  12: 'CCIR Concluído',
};
const GEO_COL_CORES: Record<number, string> = {
  1: 'var(--muted)',
  2: 'var(--accent)',
  3: '#f59e0b',
  4: '#6366f1',
  5: '#f59e0b',
  6: '#8b5cf6',
  7: 'var(--ok)',
  10: '#6366f1',
  11: '#8b5cf6',
  12: 'var(--ok)',
};
const GEO_COL_ICONS: Record<number, React.ElementType> = {
  1: Inbox,
  2: FolderOpen,
  3: MapPin,
  4: SatelliteDish,
  5: Archive,
  6: Landmark,
  7: CircleCheck,
  10: FolderOpen,
  11: Landmark,
  12: CircleCheck,
};
const GEO_TIPO_LABEL: Record<string, string> = {
  georreferenciamento: 'GEO',
  ccir: 'CCIR',
  titulacao: 'Titulação',
  outros: 'Outro',
};

function GeoCard({ p, onClick }: { p: ProcessoResumo; onClick?: () => void }) {
  const col = Number(p.kanban_coluna) || 1;
  const colNome = GEO_COL_NOMES[col] ?? `Coluna ${col}`;
  const colCor = GEO_COL_CORES[col] ?? 'var(--muted)';
  const ColIcon = GEO_COL_ICONS[col] ?? ScanLine;
  const tipoLabel = GEO_TIPO_LABEL[p.tipo_processo ?? ''];
  const isCcir = p.tipo_processo === 'ccir';
  const sla = p.sla_status ?? 'ok';
  const slaColor =
    sla === 'critico' ? 'var(--danger)' : sla === 'atencao' ? 'var(--warning)' : 'var(--muted)';
  const resumo = p.checklist_resumo;
  const pct = resumo && resumo.total > 0 ? Math.round((resumo.recebidos / resumo.total) * 100) : -1;
  const localName = p.imovel_denominacao || p.cartorio;

  return (
    <li
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={(e) => {
        if (onClick && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick();
        }
      }}
      className={`rounded-lg bg-surface px-[14px] py-3 flex flex-col gap-1.5 select-none transition-[box-shadow,border-color] duration-150 hover:shadow-[0_2px_8px_rgba(0,0,0,0.06)] ${onClick ? 'cursor-pointer' : 'cursor-default'}`}
    >
      {/* linha 1: tipo badge + coluna + dias */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          {tipoLabel && (
            <span
              className="text-[0.65rem] font-bold px-[6px] py-[2px] rounded-full shrink-0"
              style={{
                background: isCcir ? 'rgba(99,102,241,.12)' : 'rgba(217,119,87,.12)',
                color: isCcir ? '#6366f1' : 'var(--accent)',
              }}
            >
              {tipoLabel}
            </span>
          )}
          <ColIcon size={13} strokeWidth={1.5} style={{ color: colCor, flexShrink: 0 }} />
          <span className="text-[0.84rem] font-bold truncate" style={{ color: colCor }}>
            {colNome}
          </span>
        </div>
        {p.dias_na_coluna != null && (
          <span
            className={`text-[0.75rem] whitespace-nowrap shrink-0 ${sla !== 'ok' ? 'font-bold' : 'font-normal'}`}
            style={{ color: slaColor }}
          >
            {p.dias_na_coluna}d
          </span>
        )}
      </div>

      {/* linha 2: imóvel + município + matrícula */}
      <div
        className="flex items-center gap-1.5 overflow-hidden"
        style={{ fontSize: '0.8rem', color: 'var(--text2)' }}
      >
        <MapPin size={11} strokeWidth={1.5} className="text-text-muted shrink-0" />
        <span className="truncate">
          {localName || 'Imóvel não informado'}
          {p.imovel_municipio && <span className="text-text-muted"> · {p.imovel_municipio}</span>}
        </span>
        {p.imovel_matricula && (
          <span className="text-text-muted text-[0.72rem] shrink-0">Mat.{p.imovel_matricula}</span>
        )}
      </div>

      {/* linha 3: barra de checklist */}
      {pct >= 0 && (
        <div className="flex items-center gap-2">
          <div className="flex-1 h-[4px] bg-border rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-[width] duration-300"
              style={{ width: `${pct}%`, background: pct === 100 ? 'var(--ok)' : 'var(--accent)' }}
            />
          </div>
          <span className="text-[0.72rem] text-text-muted whitespace-nowrap">
            {resumo!.recebidos}/{resumo!.total} docs
          </span>
        </div>
      )}

      {/* linha 4: responsável técnico */}
      {p.responsavel_tecnico_nome && (
        <div className="flex items-center gap-1 text-text-muted" style={{ fontSize: '0.75rem' }}>
          <User size={10} strokeWidth={1.5} />
          <span>{p.responsavel_tecnico_nome}</span>
        </div>
      )}

      {/* linha 5: pendências */}
      {p.pendencias && (
        <div
          className="flex items-start gap-1.5 rounded-md px-2 py-1.5 border"
          style={{
            fontSize: '0.78rem',
            color: 'var(--danger)',
            background: 'rgba(239,68,68,.06)',
            borderColor: 'rgba(239,68,68,.15)',
            lineHeight: 1.4,
          }}
        >
          <TriangleAlert size={12} strokeWidth={1.5} className="shrink-0 mt-px" />
          <span>{p.pendencias.length > 140 ? p.pendencias.slice(0, 140) + '…' : p.pendencias}</span>
        </div>
      )}
    </li>
  );
}

function extrairMatriculaArrendamento(arr: ArrendamentoResumo): string | null {
  const vinculadas = matriculasImoveis(arr);
  if (vinculadas) return vinculadas;
  const obs = arr.observacoes || '';
  const m = obs.match(/matr[ií]cula\s+([0-9.]+)/i);
  return m?.[1] ?? null;
}

// Mesmo layout visual do card da coluna IMÓVEIS — só diferencia pela origem
// dos dados: IMÓVEIS é próprio do cliente, ARRENDAMENTO é de terceiros.
function ArrendamentoCard({
  arrendamento: arr,
  onClick,
}: {
  arrendamento: ArrendamentoResumo;
  onClick?: () => void;
}) {
  const matricula = extrairMatriculaArrendamento(arr);
  const etiqueta = arrendamentoEtiqueta(arr);
  const { lote, gleba } = extrairLoteEGlebaArrendamento(arr);
  const municipioUf = municipiosImoveis(arr);

  // Destaque: nome dos imóveis vinculados; depois Lote/Gleba. Sem isso, usa
  // matrícula como destaque (e omite o sub pra não duplicar). Fallback final
  // é "Arrendamento #N" pra contratos sem nenhum desses campos.
  let titulo: string;
  let subMatricula: string | null = null;
  const nomeImovel = tituloImoveis(arr);
  if (nomeImovel) {
    titulo = nomeImovel;
    subMatricula = matricula ? `Matrícula ${matricula}` : null;
  } else if (lote || gleba) {
    const partes: string[] = [];
    if (lote) partes.push(`Lote ${lote}`);
    if (gleba) partes.push(`Gleba ${gleba}`);
    titulo = partes.join(' · ');
    subMatricula = matricula ? `Matrícula ${matricula}` : null;
  } else if (matricula) {
    titulo = `Matrícula ${matricula}`;
  } else {
    titulo = 'Arrendamento — imóvel não identificado';
  }

  return (
    <li
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.();
        }
      }}
      className="relative w-full text-left rounded-lg bg-surface px-[14px] py-3 flex flex-col gap-1.5 shadow-[0_1px_3px_rgba(0,0,0,0.04)] cursor-pointer select-none transition-[box-shadow,transform,border-color] duration-150 hover:shadow-[0_2px_8px_rgba(0,0,0,0.06)] hover:-translate-y-px"
    >
      <span
        className="font-semibold text-text leading-snug line-clamp-2"
        style={{ fontSize: '0.85rem' }}
      >
        {titulo}
      </span>

      {subMatricula && (
        <span className="text-text-2 line-clamp-1" style={{ fontSize: '0.78rem' }}>
          {subMatricula}
        </span>
      )}

      <div className="flex flex-wrap gap-1 mt-1">
        {etiqueta && (
          <Chip tone={etiqueta.tone} size="sm" dot>
            {etiqueta.label}
          </Chip>
        )}
        {arr.area_ha != null && (
          <Chip tone="neutral" size="sm">
            {arr.area_ha.toLocaleString('pt-BR')} ha
          </Chip>
        )}
        {municipioUf && (
          <Chip tone="info" size="sm">
            {municipioUf}
          </Chip>
        )}
        {arr.arrendador_nome && (
          <Chip
            tone="accent"
            size="sm"
            className="max-w-[14rem] !overflow-hidden !text-ellipsis !whitespace-nowrap !block"
            title={formatName(arr.arrendador_nome)}
          >
            {formatName(arr.arrendador_nome)}
          </Chip>
        )}
      </div>
    </li>
  );
}

// Tom da etiqueta de status baseado no valor — alinhado com a CHECK constraint
// de contrato_arrendamento.status: ativo|vencido|rescindido|renovado.
function arrendamentoStatusTone(
  status: string | null | undefined,
): 'ok' | 'warn' | 'danger' | 'accent' | 'neutral' {
  switch ((status ?? '').toLowerCase()) {
    case 'ativo':
      return 'ok';
    case 'renovado':
      return 'accent';
    case 'vencido':
      return 'danger';
    case 'rescindido':
      return 'warn';
    default:
      return 'neutral';
  }
}

function arrendamentoStatusLabel(status: string | null | undefined): string {
  if (!status) return '';
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
}

// Lote/gleba do arrendamento: prioriza os campos estruturados do imóvel e cai
// pra extração por regex de denominação/observações (legado sem estrutura).
// Vinda do tree davi na reconciliação 2026-06-10 — comportamento superset.
function extrairLoteEGlebaArrendamento(arr: ArrendamentoResumo): {
  lote: string | null;
  gleba: string | null;
} {
  const im = arr.imovel as Record<string, unknown> | null | undefined;
  const loteEstruturado = im?.lote ? String(im.lote) : null;
  const glebaEstruturada = im?.gleba ? String(im.gleba) : null;
  if (loteEstruturado || glebaEstruturada) {
    return { lote: loteEstruturado, gleba: glebaEstruturada };
  }

  const fonte = [im?.denominacao, arr.imovel_denominacao, arr.observacoes]
    .filter(Boolean)
    .map(String)
    .join(' ');

  const loteMatch = fonte.match(/\bLote(?:\s+Rural)?\s+([A-Z0-9.-]+)/i);
  const glebaMatch = fonte.match(/\bGleba\s+([A-Z0-9.-]+)/i);
  return {
    lote: loteMatch?.[1] ?? null,
    gleba: glebaMatch?.[1] ?? null,
  };
}

// Etiqueta exibida no card: usa o status do contrato, mas com um estado
// derivado "A vencer" quando o contrato ainda vigente (ativo/renovado) vence
// em 6 meses ou menos. 'a_vencer' não existe no banco — é calculado de
// data_vencimento. Retorna null quando não há status.
function arrendamentoEtiqueta(
  arr: ArrendamentoResumo,
): { label: string; tone: 'ok' | 'warn' | 'danger' | 'accent' | 'neutral' } | null {
  const status = (arr.status ?? '').toLowerCase();
  const vigente = status === 'ativo' || status === 'renovado';
  if (vigente && arr.data_vencimento) {
    const venc = new Date(String(arr.data_vencimento));
    if (!Number.isNaN(venc.getTime())) {
      const limite = new Date();
      limite.setMonth(limite.getMonth() + 6);
      if (venc >= new Date() && venc <= limite) {
        return { label: 'A vencer', tone: 'warn' };
      }
    }
  }
  if (!status) return null;
  return { label: arrendamentoStatusLabel(status), tone: arrendamentoStatusTone(status) };
}

function ArrendamentoDetailModal({
  arrendamento,
  onClose,
}: {
  arrendamento: ArrendamentoResumo | null;
  onClose: () => void;
}) {
  if (!arrendamento) return null;
  const matricula = extrairMatriculaArrendamento(arrendamento);
  const titulo =
    tituloImoveis(arrendamento) || 'Arrendamento — imóvel não identificado';

  return (
    <Modal open={true} onOpenChange={(v) => !v && onClose()} title={titulo} size="2xl">
      <div>
        <SectionHeader icon={Handshake} title="Dados do Arrendamento" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 mb-3">
          <Field label="Matrícula" value={matricula ?? null} />
          <Field
            label="Área"
            value={
              arrendamento.area_ha != null
                ? `${arrendamento.area_ha.toLocaleString('pt-BR')} ha`
                : null
            }
          />
          <div>
            <div className="text-xs text-text-muted mb-0.5">Status</div>
            <div className="text-sm">
              {arrendamento.status ? (
                <Chip tone={arrendamentoStatusTone(arrendamento.status)} dot>
                  {arrendamentoStatusLabel(arrendamento.status)}
                </Chip>
              ) : (
                <span className="italic text-text-muted">Pendente</span>
              )}
            </div>
          </div>
          <Field
            label="Município/UF"
            value={
              municipiosImoveis(arrendamento)
            }
          />
          <Field
            label="Início"
            value={arrendamento.data_inicio ? formatDateBR(String(arrendamento.data_inicio)) : null}
          />
          <Field
            label="Vencimento"
            value={
              arrendamento.data_vencimento
                ? formatDateBR(String(arrendamento.data_vencimento))
                : null
            }
          />
          <Field
            label="Arrendador"
            value={arrendamento.arrendador_nome ? formatName(arrendamento.arrendador_nome) : null}
          />
          <Field
            label="Arrendatário"
            value={
              arrendamento.arrendatario_nome ? formatName(arrendamento.arrendatario_nome) : null
            }
          />
          {arrendamento.observacoes && (
            <div className="md:col-span-2">
              <div className="text-xs text-text-muted mb-0.5">Observações</div>
              <div className="text-sm text-text whitespace-pre-wrap">
                {arrendamento.observacoes}
              </div>
            </div>
          )}
        </div>

        {/* Seção "Contrato de Arrendamento" — 9 blocos com toggle Mostrar mais.
            Hoje a tabela contrato_arrendamento tem só ~10 campos do que o
            modelo prevê — os ~50 restantes ficam como "Pendente" e poderão
            ser preenchidos via extração de PDF do contrato no futuro. */}
        <ContratoArrendamentoAccordion arrendamento={arrendamento} />

        {/* Detalhes individuais por matrícula, mantendo o contrato conjunto. */}
        {imoveisDoContrato(arrendamento).length ? (
          <ArrendamentoImoveis arrendamento={arrendamento} />
        ) : (
          <ImovelArrendadoEmptyState arrendamento={arrendamento} />
        )}
      </div>
    </Modal>
  );
}

// Seção "Contrato de Arrendamento" — Accordion com 9 sub-blocos baseados na
// proposta visual (Seção Contrato — Kanban Cliente). Hoje a tabela
// contrato_arrendamento tem ~10 dos ~60 campos previstos. O resto vai como
// "Pendente" e poderá ser preenchido via extração de PDF do contrato.
function ContratoArrendamentoAccordion({ arrendamento }: { arrendamento: ArrendamentoResumo }) {
  // Campos diretos disponíveis hoje
  const inicio = arrendamento.data_inicio ? formatDateBR(String(arrendamento.data_inicio)) : null;
  const vencimento = arrendamento.data_vencimento
    ? formatDateBR(String(arrendamento.data_vencimento))
    : null;
  const areaArr =
    arrendamento.area_ha != null ? `${arrendamento.area_ha.toLocaleString('pt-BR')} ha` : null;
  const valorPorHa = (arrendamento as { valor_por_ha?: number | string | null }).valor_por_ha;
  const status = arrendamento.status ?? null;
  const arrendador = arrendamento.arrendador_nome ? formatName(arrendamento.arrendador_nome) : null;
  const arrendatario = arrendamento.arrendatario_nome
    ? formatName(arrendamento.arrendatario_nome)
    : null;
  const matricula = extrairMatriculaArrendamento(arrendamento);
  const munUf = municipiosImoveis(arrendamento);
  const imovelDenom = tituloImoveis(arrendamento)
    ? tituloImoveis(arrendamento)
    : null;

  // Duração derivada (anos completos entre início/vencimento)
  let duracao: string | null = null;
  if (arrendamento.data_inicio && arrendamento.data_vencimento) {
    const ini = new Date(String(arrendamento.data_inicio));
    const fim = new Date(String(arrendamento.data_vencimento));
    if (!Number.isNaN(ini.getTime()) && !Number.isNaN(fim.getTime())) {
      const anos = Math.round((fim.getTime() - ini.getTime()) / (365.25 * 24 * 3600 * 1000));
      if (anos > 0) duracao = `${anos} ${anos === 1 ? 'ano' : 'anos'}`;
    }
  }

  return (
    <Accordion icon={Handshake} title="Contrato de Arrendamento" tone="accent" subtitle={status}>
      <div className="flex flex-col gap-4">
        {/* 1. Identificação */}
        <SubBlock title="1. Identificação do contrato">
          <ContratoFieldGrid
            fields={[
              ['Nº / código', `CTR-${arrendamento.id}`],
              ['Tipo', null],
              ['Objeto', null],
              ['Status', status],
              ['Safra vigente', null],
              ['Data de assinatura', null],
            ]}
          />
        </SubBlock>

        {/* 2. Partes envolvidas */}
        <SubBlock title="2. Partes envolvidas">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <PartySubCard
              tag="Arrendador(a)"
              items={[
                ['Nome completo', arrendador],
                ['Nacionalidade / estado civil', null],
                ['Profissão', null],
                ['RG e órgão emissor', null],
                ['CPF / CNPJ', null],
                ['Endereço completo + CEP', null],
                ['Cônjuge (se houver)', null],
                ['Filiação', null],
                ['E-mail / telefone', null],
              ]}
            />
            <PartySubCard
              tag="Arrendatário(a)"
              items={[
                ['Nome completo', arrendatario],
                ['Nacionalidade / estado civil', null],
                ['Profissão', null],
                ['RG e órgão emissor', null],
                ['CPF / CNPJ', null],
                ['Endereço completo + CEP', null],
                ['Cônjuge (se houver)', null],
                ['E-mail / telefone', null],
              ]}
            />
            <PartySubCard
              tag="Anuentes / proprietários"
              items={[
                ['Nome (em caso de usufruto)', null],
                ['RG / CPF', null],
                ['Endereço', null],
                ['Tipo de anuência', null],
                ['Cônjuge anuente', null],
                ['Sub-rogação contratual', null],
              ]}
            />
          </div>
        </SubBlock>

        {/* 3. Imóvel(eis) arrendado(s) */}
        <SubBlock title="3. Imóvel(eis) arrendado(s)">
          <ContratoFieldGrid
            fields={[
              ['Denominação', imovelDenom],
              ['Matrícula CRI', matricula ?? null],
              ['Código CCIR', null],
              ['Cartório / comarca', null],
              ['Localização', null],
              ['Município / UF / CEP', munUf],
              ['Área total', null],
              ['Área arrendada', areaArr],
              ['Culturas permitidas', null],
            ]}
          />
        </SubBlock>

        {/* 4 + 5 lado a lado */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <SubBlock title="4. Vigência">
            <ContratoFieldGrid
              fields={[
                ['Data de início', inicio],
                ['Data de término', vencimento],
                ['Duração', duracao],
                ['Prorrogação', null],
                ['Direito de preferência', null],
                ['Foro', null],
              ]}
            />
          </SubBlock>

          <SubBlock title="5. Pagamento / renda">
            <ContratoFieldGrid
              fields={[
                ['Forma', null],
                ['Produto', null],
                ['Quantidade por safra', null],
                ['Peso da saca', null],
                ['Qualidade exigida', null],
                ['Local / armazém', null],
                ['Datas de vencimento', null],
                ['Parcelamento', null],
                ['Multa por descumprimento', null],
                ['Reajuste / aditivo', null],
                [
                  'Valor por hectare',
                  valorPorHa != null ? `R$ ${Number(valorPorHa).toLocaleString('pt-BR')}` : null,
                ],
              ]}
            />
          </SubBlock>
        </div>

        {/* 6 + 7 lado a lado */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <SubBlock title="6. Garantias e cláusulas">
            <ContratoBulletList
              items={[
                'Anuência para penhor agrícola',
                'Vedação de hipoteca do imóvel',
                'Anuência financeira (Banco da Amazônia / outros)',
                'Responsabilidade por insumos / mão de obra / maquinário',
                'Conservação do solo e meio ambiente',
                'Multas IBAMA / SEDAM / Min. do Trabalho / MP',
                'Vedação de cessão / sublocação',
                'Embargo: ressarcimento ao arrendatário',
                'Caráter irrevogável e irretratável',
                'Acesso livre do arrendador à área',
              ]}
            />
          </SubBlock>

          <SubBlock title="7. Documentos anexos">
            <ContratoBulletList
              items={[
                'Contrato assinado (PDF)',
                'Carta de anuência (banco)',
                'Matrícula atualizada do imóvel',
                'CCIR vigente',
                'RG e CPF das partes',
                'Comprovante de endereço',
                'Aditivos / renovações',
                'Comprovantes de entrega da safra',
                'Baixa de penhor agrícola',
              ]}
            />
          </SubBlock>
        </div>

        {/* 8 + 9 lado a lado */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <SubBlock title="8. Cartório e registro">
            <ContratoFieldGrid
              fields={[
                ['Cartório registrador', null],
                ['Selo digital', null],
                ['Protocolo / livro', null],
                ['Data do registro', null],
                ['Testemunha 1', null],
                ['Testemunha 2', null],
              ]}
            />
          </SubBlock>

          <SubBlock title="9. Alertas e acompanhamento">
            <div className="flex flex-col gap-1.5 text-[0.82rem]">
              <ContratoAlerta tone="warn">Próximo vencimento da renda</ContratoAlerta>
              <ContratoAlerta tone="info">Janela de renovação / aditivo</ContratoAlerta>
              <ContratoAlerta tone="danger">Pendências de baixa de penhor</ContratoAlerta>
              <ContratoAlerta tone="ok">Safra entregue / pagamento ok</ContratoAlerta>
              <ContratoAlerta tone="muted">Histórico de safras anteriores</ContratoAlerta>
            </div>
          </SubBlock>
        </div>
      </div>
    </Accordion>
  );
}

// Sub-bloco com título — usado dentro do Accordion de Contrato. Sem margin
// próprio: o spacing entre blocos é dado pelo wrapper flex-col gap-4 acima.
// Com margin individual, blocos lado a lado em grid 2-cols ficam desalinhados.
function SubBlock({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h4 className="text-[0.85rem] font-semibold text-text mb-2">{title}</h4>
      {children}
    </div>
  );
}

// Grid de campos label/valor em cards leves.
function ContratoFieldGrid({ fields }: { fields: Array<[string, string | null]> }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
      {fields.map(([label, value]) => (
        <div key={label} className="rounded-md bg-surface2 px-2.5 py-1.5">
          <div className="text-[0.7rem] text-text-muted leading-tight">{label}</div>
          <div className="text-[0.82rem] text-text leading-snug mt-0.5">
            {value ? value : <span className="italic text-text-muted">Pendente</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

function ContratoBulletList({ items }: { items: string[] }) {
  return (
    <ul className="text-[0.82rem] leading-relaxed list-disc pl-5 text-text-2">
      {items.map((it) => (
        <li key={it}>{it}</li>
      ))}
    </ul>
  );
}

function ContratoAlerta({
  tone,
  children,
}: {
  tone: 'info' | 'ok' | 'warn' | 'danger' | 'muted';
  children: React.ReactNode;
}) {
  const cls: Record<typeof tone, string> = {
    info: 'bg-[rgba(24,95,165,0.10)] text-[#185FA5]',
    ok: 'bg-[rgba(59,109,17,0.10)] text-[#3B6D11]',
    warn: 'bg-[rgba(133,79,11,0.10)] text-[#854F0B]',
    danger: 'bg-[rgba(163,45,45,0.10)] text-[#A32D2D]',
    muted: 'bg-surface2 text-text-2',
  };
  return <div className={`rounded-md px-2.5 py-1.5 ${cls[tone]}`}>{children}</div>;
}

// Sub-card de parte (Arrendador / Arrendatário / Anuente).
function PartySubCard({ tag, items }: { tag: string; items: Array<[string, string | null]> }) {
  return (
    <div className="rounded-md border border-border p-2.5 bg-elevated">
      <div className="text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted mb-2">
        {tag}
      </div>
      <ul className="space-y-1 text-[0.78rem]">
        {items.map(([label, value]) => (
          <li key={label} className="leading-snug">
            <span className="text-text-muted">{label}: </span>
            {value ? (
              <span className="text-text">{value}</span>
            ) : (
              <span className="italic text-text-muted">Pendente</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

// Empty state quando arrendamento.imovel === null — sinaliza que a área de
// terceiro nunca foi cadastrada como imóvel. Mostra os campos esperados
// (Certidão/CAR/CCIR/Memorial/ITR) bloqueados e oferece CTA para cadastrar.
// Implementação do cadastro vem na Fase 3.
function ImovelArrendadoEmptyState({ arrendamento }: { arrendamento: ArrendamentoResumo }) {
  return (
    <div className="mt-4 pt-4 border-t border-border">
      <SectionHeader icon={Building2} title="Detalhes do Imóvel Arrendado" />
      <div className="rounded-lg border border-dashed border-border bg-elevated px-4 py-5">
        <div className="flex items-start gap-3">
          <div className="rounded-md bg-surface2 p-2">
            <Building2 size={16} strokeWidth={1.5} className="text-text-2" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-text">
              Imóvel ainda não vinculado ao contrato
            </div>
            <p className="text-xs text-text-2 mt-1">
              Para preencher Certidão de Inteiro Teor, CAR, CCIR, Memorial Descritivo e ITR,
              localize o imóvel{' '}
              {arrendamento.arrendador_nome
                ? `de ${formatName(arrendamento.arrendador_nome)}`
                : 'do arrendador'}{' '}
              e vincule ao contrato. A ausência de vínculo não significa que o imóvel não esteja cadastrado.
            </p>
            <button
              type="button"
              disabled
              className="mt-3 inline-flex items-center gap-1.5 px-3 h-8 rounded-md text-[0.78rem] font-medium border border-border bg-surface text-text-2 cursor-not-allowed opacity-60"
              title="Disponível na próxima entrega (Fase 3)"
            >
              <Building2 size={13} strokeWidth={1.5} />
              Cadastrar imóvel do arrendador
            </button>
            <p className="text-[0.7rem] text-text-muted mt-2 italic">
              Em breve — cadastro de imóvel de terceiro
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Field — label em cima, valor embaixo (padrão FieldGrid do ImovelDetailModal).
// Quando value é null/vazio, renderiza "Pendente" em itálico/muted.
function Field({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <div className="text-xs text-text-muted mb-0.5">{label}</div>
      <div className="text-sm text-text">
        {value ? value : <span className="italic text-text-muted">Pendente</span>}
      </div>
    </div>
  );
}

function DocumentoPreviewModal({
  docId,
  onClose,
}: {
  docId: string | number;
  onClose: () => void;
}) {
  const url = `/api/v2/documentos/${docId}/arquivo`;
  return (
    <Modal
      open={true}
      onOpenChange={(v) => !v && onClose()}
      title="Visualizar documento"
      size="3xl"
      headerActions={
        <>
          <Button variant="ghost" asChild>
            <a href={url} target="_blank" rel="noreferrer">
              Abrir em nova aba
            </a>
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Fechar
          </Button>
        </>
      }
    >
      <iframe src={url} className="w-full h-[70vh] border-0 rounded" title="documento" />
    </Modal>
  );
}

interface SectionProps {
  title: string;
  icon: LucideIcon;
  count: number;
  subtitle?: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
}

// Lane do padrão Kanban ARA — delega ao primitivo KanbanLane (estilo único do
// design system). `subtitle` vira `meta` (prefixado com ·), `action` vira o
// slot à direita do header.
function Section({ title, icon, count, subtitle, action, children }: SectionProps) {
  return (
    <KanbanLane
      icon={icon}
      title={title}
      count={count}
      meta={subtitle != null ? <>· {subtitle}</> : undefined}
      headerRight={action}
    >
      {children}
    </KanbanLane>
  );
}
